
import { SubscriptionPlan, subscriptionPlans } from '@/types/subscriptionPlans';

const STORAGE_KEY = 'business_signup_plans';

/**
 * Fetches the business signup plans from localStorage or falls back to default plans
 */
export const getBusinessSignupPlans = (): SubscriptionPlan[] => {
  try {
    const savedPlans = localStorage.getItem(STORAGE_KEY);
    if (savedPlans) {
      return JSON.parse(savedPlans);
    }
  } catch (error) {
    console.error('Error parsing saved business signup plans:', error);
  }
  return subscriptionPlans;
};

/**
 * Saves the business signup plans to localStorage and triggers a custom event
 * to notify subscribers about the changes
 */
export const saveBusinessSignupPlans = (plans: SubscriptionPlan[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
    
    // Dispatch a custom event to notify subscribers that plans have been updated
    const event = new CustomEvent('business-plans-updated', { detail: plans });
    window.dispatchEvent(event);
  } catch (error) {
    console.error('Error saving business signup plans:', error);
  }
};

/**
 * Subscribes to business plan changes
 * @param callback Function to call when plans are updated
 * @returns Cleanup function to remove the event listener
 */
export const subscribeToBusinessPlanChanges = (callback: (plans: SubscriptionPlan[]) => void): () => void => {
  const handlePlanUpdate = (event: Event) => {
    const customEvent = event as CustomEvent<SubscriptionPlan[]>;
    callback(customEvent.detail);
  };
  
  window.addEventListener('business-plans-updated', handlePlanUpdate);
  return () => window.removeEventListener('business-plans-updated', handlePlanUpdate);
};

/**
 * Gets a specific plan by ID
 * @param planId The ID of the plan to retrieve
 * @returns The plan if found, otherwise null
 */
export const getBusinessPlanById = (planId: string): SubscriptionPlan | null => {
  const plans = getBusinessSignupPlans();
  return plans.find(plan => plan.id === planId) || null;
};
